import axios from 'axios';
import { apiClient } from './client';
import type {
  FallbackInviteCreate,
  FallbackInviteArtifactsResponse,
  Invite,
  TokenResponse,
  FallbackActivateRequest,
  FallbackLoginRequest,
  FallbackAuthError,
  FallbackAuthErrorCode,
  AuditQueryParams,
  AuditListResponse,
} from './types';

export const createFallbackInvite = async (
  data: FallbackInviteCreate
): Promise<FallbackInviteArtifactsResponse> => {
  const response = await apiClient.post<FallbackInviteArtifactsResponse>(
    '/auth/invites/fallback',
    data
  );
  return response.data;
};

export const regenerateFallbackInvite = async (
  inviteId: number
): Promise<FallbackInviteArtifactsResponse> => {
  const response = await apiClient.post<FallbackInviteArtifactsResponse>(
    `/auth/invites/fallback/${inviteId}/regenerate`
  );
  return response.data;
};

export const revokeFallbackInvite = async (inviteId: number): Promise<Invite> => {
  const response = await apiClient.post<Invite>(`/auth/invites/fallback/${inviteId}/revoke`);
  return response.data;
};

export const activateFallbackAccount = async (
  data: FallbackActivateRequest
): Promise<TokenResponse> => {
  const response = await apiClient.post<TokenResponse>('/auth/fallback/activate', data);
  return response.data;
};

export const fallbackLogin = async (data: FallbackLoginRequest): Promise<TokenResponse> => {
  const response = await apiClient.post<TokenResponse>('/auth/fallback/login', data);
  return response.data;
};

export const getAuditEvents = async (params?: AuditQueryParams): Promise<AuditListResponse> => {
  const response = await apiClient.get<AuditListResponse>('/auth/audit', { params });
  return response.data;
};

const ERROR_MESSAGE_TO_CODE: Record<string, FallbackAuthErrorCode> = {
  'fallback authentication is disabled': 'FALLBACK_DISABLED',
  'invite has been revoked': 'INVITE_REVOKED',
  'invite has expired': 'INVITE_EXPIRED',
  'activation link has expired': 'INVITE_EXPIRED',
  'account already activated': 'ACTIVATION_ALREADY_USED',
  'invalid activation code': 'INVALID_CODE',
  'invalid credentials': 'INVALID_CREDENTIALS',
  'invalid email or password': 'INVALID_CREDENTIALS',
  'account is locked': 'ACCOUNT_LOCKED',
  'too many failed attempts': 'ACCOUNT_LOCKED',
};

const RETRYABLE_CODES: ReadonlySet<FallbackAuthErrorCode> = new Set([
  'INVALID_CREDENTIALS',
  'INVALID_CODE',
]);

function resolveErrorCode(statusCode: number, message: string): FallbackAuthErrorCode {
  const lowerMessage = message.toLowerCase();
  for (const [pattern, code] of Object.entries(ERROR_MESSAGE_TO_CODE)) {
    if (lowerMessage.includes(pattern)) {
      return code;
    }
  }
  if (statusCode === 401) return 'INVALID_CREDENTIALS';
  if (statusCode === 403) return 'FALLBACK_DISABLED';
  if (statusCode === 410) return 'INVITE_EXPIRED';
  if (statusCode === 409) return 'ACTIVATION_ALREADY_USED';
  if (statusCode === 429) return 'ACCOUNT_LOCKED';
  return 'UNKNOWN';
}

export function parseFallbackAuthError(error: unknown): FallbackAuthError {
  if (axios.isAxiosError(error) && error.response) {
    const statusCode = error.response.status;
    const responseData = error.response.data as { message?: string | string[] } | undefined;
    const rawMessage = responseData?.message;
    const message = Array.isArray(rawMessage)
      ? (rawMessage[0] ?? 'Request failed')
      : (rawMessage ?? 'Request failed');
    const code = resolveErrorCode(statusCode, message);

    return {
      code,
      message,
      statusCode,
      retryable: RETRYABLE_CODES.has(code),
    };
  }

  return {
    code: 'UNKNOWN',
    message: error instanceof Error ? error.message : 'An unexpected error occurred',
    statusCode: 0,
    retryable: false,
  };
}
